import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as Tone from 'tone';
import './composer-page.css';
import { EVENTS } from '../../../events/events';
import { triggerAttackAll, triggerReleaseAll, triggerAttackReleaseAll, releaseAllSynths } from '../../../synth';
import { useLocalStorage } from '../../../hooks/use-local-storage';
import { DawNote } from '../../../types';

// ── Constants ──────────────────────────────────────────────────────────────────

const STEP_WIDTH = 25;
const ROW_HEIGHT = 18;
const LABEL_WIDTH = 44;
const HEADER_HEIGHT = 24;

const STEPS_PER_BEAT = 4;
const BEATS_PER_MEASURE = 4;
const STEPS_PER_MEASURE = STEPS_PER_BEAT * BEATS_PER_MEASURE;

const MIN_BPM = 40;
const MAX_BPM = 240;
const DEFAULT_BPM = 120;
const DEFAULT_MEASURES = 4;
const MIN_MEASURES = 1;
const MAX_MEASURES = 32;

const NOTE_DURATIONS = [
  { label: '1/1',  steps: STEPS_PER_MEASURE },
  { label: '1/2',  steps: STEPS_PER_MEASURE / 2 },
  { label: '1/4',  steps: STEPS_PER_BEAT },
  { label: '1/8',  steps: STEPS_PER_BEAT / 2 },
  { label: '1/16', steps: 1 },
] as const;

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// All notes from highest (B7) to lowest (C0)
const ALL_NOTES: string[] = [];
for (let octave = 7; octave >= 0; octave--) {
  for (let i = 11; i >= 0; i--) {
    ALL_NOTES.push(`${NOTE_NAMES[i]}${octave}`);
  }
}

const TOTAL_NOTES = ALL_NOTES.length;

function isSharpNote(note: string): boolean {
  return note.includes('#');
}

function noteToRow(note: string): number {
  return ALL_NOTES.indexOf(note);
}

// ── Types ──────────────────────────────────────────────────────────────────────

interface DragState {
  noteId: string;
  startX: number;
  originalDuration: number;
}

type TransportState = 'idle' | 'playing' | 'recording';

// ── Component ──────────────────────────────────────────────────────────────────

export const ComposerPage: React.FC = () => {
  const [bpm, setBpm] = useLocalStorage<number>('synthkitty:bpm', DEFAULT_BPM);
  const [measures, setMeasures] = useLocalStorage<number>('synthkitty:daw-measures', DEFAULT_MEASURES);
  const [notes, setNotes] = useLocalStorage<DawNote[]>('synthkitty:daw-notes', []);
  const [loop, setLoop] = useLocalStorage<boolean>('synthkitty:daw-loop', true);
  const [noteDurationIndex, setNoteDurationIndex] = useLocalStorage<number>('synthkitty:daw-note-duration', 2);
  const [transportState, setTransportState] = useState<TransportState>('idle');
  const [countdown, setCountdown] = useState<number | null>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [pendingSeekMeasure, setPendingSeekMeasure] = useState<number | null>(null);
  const dragMovedRef = useRef(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const previewNoteRef = useRef<string | null>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bpmRef = useRef(bpm);
  const measuresRef = useRef(measures);
  const notesRef = useRef(notes);
  const loopRef = useRef(loop);
  const recordingNoteStartsRef = useRef<Map<string, number>>(new Map());
  const pendingSeekRef = useRef<number | null>(null);
  const pendingSeekEventRef = useRef<number | null>(null);

  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  useEffect(() => { measuresRef.current = measures; }, [measures]);
  useEffect(() => { notesRef.current = notes; }, [notes]);
  useEffect(() => { loopRef.current = loop; }, [loop]);

  const totalSteps = measures * STEPS_PER_MEASURE;
  const gridWidth = totalSteps * STEP_WIDTH;
  const gridHeight = TOTAL_NOTES * ROW_HEIGHT;

  // Scroll to C3–C5 range on mount
  useEffect(() => {
    if (scrollRef.current) {
      const c4Row = ALL_NOTES.indexOf('C4');
      scrollRef.current.scrollTop = Math.max(0, (c4Row - 10) * ROW_HEIGHT);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startRecordingRef = useRef<() => Promise<void>>(async () => {});
  const startPlaybackRef = useRef<(startMeasure?: number) => Promise<void>>(async () => {});

  // ── BPM ────────────────────────────────────────────────────────────────────

  const handleBpmChange = useCallback((val: number) => {
    const clamped = Math.min(MAX_BPM, Math.max(MIN_BPM, isNaN(val) ? DEFAULT_BPM : val));
    setBpm(clamped);
  }, [setBpm]);

  // ── Measures ───────────────────────────────────────────────────────────────

  const handleMeasuresChange = useCallback((val: number) => {
    const clamped = Math.min(MAX_MEASURES, Math.max(MIN_MEASURES, isNaN(val) ? DEFAULT_MEASURES : val));
    const maxStep = clamped * STEPS_PER_MEASURE;
    setNotes(prev =>
      prev
        .filter(n => n.startStep < maxStep)
        .map(n => ({
          ...n,
          durationSteps: Math.min(n.durationSteps, maxStep - n.startStep),
        }))
    );
    setMeasures(clamped);
  }, [setMeasures, setNotes]);

  // ── Playhead animation ─────────────────────────────────────────────────────

  const startPlayheadAnimation = useCallback(() => {
    const tick = () => {
      if (!playheadRef.current) return;
      const stepDuration = 60 / bpmRef.current / STEPS_PER_BEAT;
      const total = measuresRef.current * STEPS_PER_MEASURE * stepDuration;
      const seconds = Tone.getTransport().seconds;
      const pos = loopRef.current ? (seconds % total) : Math.min(seconds, total);
      const x = (pos / stepDuration) * STEP_WIDTH;
      playheadRef.current.style.left = `${x}px`;
      playheadRef.current.style.display = 'block';

      // Auto-scroll to keep playhead visible
      if (scrollRef.current) {
        const scroll = scrollRef.current;
        const viewLeft = scroll.scrollLeft;
        const viewRight = viewLeft + scroll.clientWidth - LABEL_WIDTH;
        if (x > viewRight - 40) {
          scroll.scrollLeft = x - 40;
        } else if (x < viewLeft + 20) {
          scroll.scrollLeft = Math.max(0, x - 40);
        }
      }

      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
  }, []);

  const stopPlayheadAnimation = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (playheadRef.current) {
      playheadRef.current.style.display = 'none';
    }
  }, []);

  // ── Stop ───────────────────────────────────────────────────────────────────

  const stopTransport = useCallback(() => {
    setCountdown(null);
    pendingSeekRef.current = null;
    if (pendingSeekEventRef.current !== null) {
      Tone.getTransport().clear(pendingSeekEventRef.current);
      pendingSeekEventRef.current = null;
    }
    setPendingSeekMeasure(null);
    Tone.getTransport().stop();
    Tone.getTransport().cancel();
    Tone.getTransport().loop = false;
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    stopPlayheadAnimation();
    recordingNoteStartsRef.current.clear();
    window.dispatchEvent(new CustomEvent(EVENTS.COMPOSER_STOPPED));
    setTransportState('idle');
  }, [stopPlayheadAnimation]);

  // ── Playback ───────────────────────────────────────────────────────────────

  const startPlayback = useCallback(async (startMeasure = 0) => {
    await Tone.start();
    const transport = Tone.getTransport();
    transport.stop();
    transport.cancel();
    releaseAllSynths();
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    transport.bpm.value = bpmRef.current;

    const stepDur = 60 / bpmRef.current / STEPS_PER_BEAT;
    const totalDur = measuresRef.current * STEPS_PER_MEASURE * stepDur;
    const startOffset = startMeasure * STEPS_PER_MEASURE * stepDur;

    notesRef.current.forEach(n => {
      transport.schedule((time: number) => {
        const duration = Math.max(0.05, n.durationSteps * stepDur);
        triggerAttackReleaseAll(n.note, duration, time);
        window.dispatchEvent(new CustomEvent(EVENTS.COMPOSER_NOTE_ON, { detail: n.note }));
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent(EVENTS.COMPOSER_NOTE_OFF, { detail: n.note }));
        }, duration * 1000);
      }, n.startStep * stepDur);
    });

    if (loopRef.current) {
      transport.loop = true;
      transport.loopStart = 0;
      transport.loopEnd = totalDur;
    } else {
      transport.loop = false;
      const remainingDur = totalDur - startOffset;
      stopTimerRef.current = setTimeout(() => stopTransport(), (remainingDur + 0.3) * 1000);
    }

    if (scrollRef.current) scrollRef.current.scrollLeft = startMeasure * STEPS_PER_MEASURE * STEP_WIDTH;
    transport.start('+0', startOffset);
    setTransportState('playing');
    startPlayheadAnimation();
  }, [stopTransport, startPlayheadAnimation]);

  // ── Recording ──────────────────────────────────────────────────────────────

  const startRecording = useCallback(async () => {
    await Tone.start();
    const transport = Tone.getTransport();
    transport.stop();
    transport.cancel();
    transport.bpm.value = bpmRef.current;

    const stepDur = 60 / bpmRef.current / STEPS_PER_BEAT;
    const totalDur = measuresRef.current * STEPS_PER_MEASURE * stepDur;

    notesRef.current.forEach(n => {
      transport.schedule((time: number) => {
        const duration = Math.max(0.05, n.durationSteps * stepDur);
        triggerAttackReleaseAll(n.note, duration, time);
        window.dispatchEvent(new CustomEvent(EVENTS.COMPOSER_NOTE_ON, { detail: n.note }));
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent(EVENTS.COMPOSER_NOTE_OFF, { detail: n.note }));
        }, duration * 1000);
      }, n.startStep * stepDur);
    });

    if (loopRef.current) {
      transport.loop = true;
      transport.loopStart = 0;
      transport.loopEnd = totalDur;
    } else {
      transport.loop = false;
      stopTimerRef.current = setTimeout(() => stopTransport(), (totalDur + 0.3) * 1000);
    }

    recordingNoteStartsRef.current.clear();
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    transport.start();
    setTransportState('recording');
    startPlayheadAnimation();
  }, [stopTransport, startPlayheadAnimation]);

  startRecordingRef.current = startRecording;
  startPlaybackRef.current = startPlayback;

  // ── Countdown ──────────────────────────────────────────────────────────────

  const playCountdownClick = useCallback(async () => {
    await Tone.start();
    const click = new Tone.Synth({
      oscillator: { type: 'triangle' },
      envelope: { attack: 0.001, decay: 0.07, sustain: 0, release: 0.01 },
      volume: -4,
    }).toDestination();
    click.triggerAttackRelease('C6', '32n');
    setTimeout(() => click.dispose(), 500);
  }, []);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) {
      setCountdown(null);
      startRecordingRef.current();
      return;
    }
    playCountdownClick();
    const beatMs = (60 / bpmRef.current) * 1000;
    const timer = setTimeout(() => setCountdown(c => (c ?? 1) - 1), beatMs);
    return () => clearTimeout(timer);
  }, [countdown, playCountdownClick]);

  // ── Piano event capture during recording ───────────────────────────────────

  useEffect(() => {
    if (transportState !== 'recording') return;

    const handleKeyDown = (e: Event) => {
      const note = (e as CustomEvent<string>).detail;
      if (recordingNoteStartsRef.current.has(note)) return;
      const stepDur = 60 / bpmRef.current / STEPS_PER_BEAT;
      const totalSteps = measuresRef.current * STEPS_PER_MEASURE;
      const totalDur = totalSteps * stepDur;
      const seconds = Tone.getTransport().seconds;
      const pos = loopRef.current ? (seconds % totalDur) : seconds;
      const currentStep = Math.round(pos / stepDur);
      if (currentStep < totalSteps) {
        recordingNoteStartsRef.current.set(note, currentStep);
      }
    };

    const handleKeyUp = (e: Event) => {
      const note = (e as CustomEvent<string>).detail;
      const startStep = recordingNoteStartsRef.current.get(note);
      if (startStep === undefined) return;
      const stepDur = 60 / bpmRef.current / STEPS_PER_BEAT;
      const totalSteps = measuresRef.current * STEPS_PER_MEASURE;
      const totalDur = totalSteps * stepDur;
      const seconds = Tone.getTransport().seconds;
      const pos = loopRef.current ? (seconds % totalDur) : seconds;
      let endStep = Math.round(pos / stepDur);
      let dur = endStep - startStep;
      if (dur <= 0) dur = 1;
      dur = Math.min(dur, totalSteps - startStep);
      if (dur > 0 && startStep < totalSteps) {
        setNotes(prev => [
          ...prev,
          {
            id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            note,
            startStep,
            durationSteps: dur,
          },
        ]);
      }
      recordingNoteStartsRef.current.delete(note);
    };

    window.addEventListener(EVENTS.PIANO_KEY_DOWN, handleKeyDown);
    window.addEventListener(EVENTS.PIANO_KEY_UP, handleKeyUp);
    return () => {
      window.removeEventListener(EVENTS.PIANO_KEY_DOWN, handleKeyDown);
      window.removeEventListener(EVENTS.PIANO_KEY_UP, handleKeyUp);
    };
  }, [transportState, setNotes]);

  // ── Cleanup ────────────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      Tone.getTransport().stop();
      Tone.getTransport().cancel();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    };
  }, []);

  // ── Grid click: add / remove / resize notes ───────────────────────────────
  // Click empty cell → add note. Click note without dragging → remove.
  // Click + drag on a note → resize (adjusts right edge).

  const handleGridPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (transportState !== 'idle') return;
      if (!gridRef.current) return;

      const rect = gridRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const step = Math.floor(x / STEP_WIDTH);
      const row = Math.floor(y / ROW_HEIGHT);

      if (row < 0 || row >= TOTAL_NOTES) return;
      if (step < 0 || step >= totalSteps) return;

      const noteName = ALL_NOTES[row];

      const clickedNote = notes.find(
        n => n.note === noteName && step >= n.startStep && step < n.startStep + n.durationSteps,
      );

      if (clickedNote) {
        // Enter drag state — decision (remove vs resize) is made on pointer up
        dragMovedRef.current = false;
        setDragState({ noteId: clickedNote.id, startX: x, originalDuration: clickedNote.durationSteps });
        e.preventDefault();
      } else {
        const dur = Math.min(NOTE_DURATIONS[noteDurationIndex].steps, totalSteps - step);
        if (dur > 0) {
          setNotes(prev => [
            ...prev,
            {
              id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              note: noteName,
              startStep: step,
              durationSteps: dur,
            },
          ]);
          // Preview the note while pointer is held
          triggerAttackAll(noteName, Tone.now());
          previewNoteRef.current = noteName;
        }
      }
    },
    [transportState, notes, totalSteps, setNotes, noteDurationIndex],
  );

  // Stop note preview on pointer up anywhere
  useEffect(() => {
    const stopPreview = () => {
      if (previewNoteRef.current) {
        triggerReleaseAll(previewNoteRef.current!, Tone.now());
        previewNoteRef.current = null;
      }
    };
    window.addEventListener('pointerup', stopPreview);
    return () => window.removeEventListener('pointerup', stopPreview);
  }, []);

  // ── Drag handler: resize on move, remove on click ─────────────────────────

  useEffect(() => {
    if (!dragState) return;

    const handleMove = (e: PointerEvent) => {
      if (!gridRef.current) return;
      const rect = gridRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;

      // Only start resizing after moving at least 4px
      if (!dragMovedRef.current && Math.abs(x - dragState.startX) <= 3) return;
      dragMovedRef.current = true;

      const delta = Math.round((x - dragState.startX) / STEP_WIDTH);
      const newDur = Math.max(1, dragState.originalDuration + delta);
      setNotes(prev =>
        prev.map(n => {
          if (n.id !== dragState.noteId) return n;
          return { ...n, durationSteps: Math.min(newDur, measuresRef.current * STEPS_PER_MEASURE - n.startStep) };
        }),
      );
    };

    const handleUp = () => {
      if (!dragMovedRef.current) {
        // No drag happened → remove the note
        setNotes(prev => prev.filter(n => n.id !== dragState.noteId));
      }
      setDragState(null);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [dragState, setNotes]);

  // ── Memoised grid backgrounds ──────────────────────────────────────────────

  const rowBackgrounds = useMemo(
    () =>
      ALL_NOTES.map((note, row) => {
        const sharp = isSharpNote(note);
        const isC = note.startsWith('C') && !note.startsWith('C#');
        const cls = `sk-daw-row${sharp ? ' sk-daw-row--sharp' : ''}${isC ? ' sk-daw-row--c' : ''}`;
        return <div key={note} className={cls} style={{ top: row * ROW_HEIGHT, height: ROW_HEIGHT, width: gridWidth }} />;
      }),
    [gridWidth],
  );

  const verticalLines = useMemo(
    () =>
      Array.from({ length: totalSteps + 1 }, (_, i) => {
        const isMeasure = i % STEPS_PER_MEASURE === 0;
        const isBeat = !isMeasure && i % STEPS_PER_BEAT === 0;
        const cls = `sk-daw-vline${isMeasure ? ' sk-daw-vline--measure' : isBeat ? ' sk-daw-vline--beat' : ''}`;
        return <div key={i} className={cls} style={{ left: i * STEP_WIDTH, height: gridHeight }} />;
      }),
    [totalSteps, gridHeight],
  );

  const labelElements = useMemo(
    () =>
      ALL_NOTES.map(note => {
        const sharp = isSharpNote(note);
        const isC = note.startsWith('C') && !note.startsWith('C#');
        const cls = `sk-daw-label${sharp ? ' sk-daw-label--sharp' : ''}${isC ? ' sk-daw-label--c' : ''}`;
        return (
          <div key={note} className={cls} style={{ height: ROW_HEIGHT }}>
            <span className="pixel-font">{note}</span>
          </div>
        );
      }),
    [],
  );

  // ── Header seek ────────────────────────────────────────────────────────────

  const handleHeaderClick = useCallback(
    (measureIndex: number) => {
      if (transportState === 'recording') return;
      if (transportState === 'playing') {
        // Clear any existing pending seek event
        if (pendingSeekEventRef.current !== null) {
          Tone.getTransport().clear(pendingSeekEventRef.current);
          pendingSeekEventRef.current = null;
        }

        const stepDur = 60 / bpmRef.current / STEPS_PER_BEAT;
        const measureDur = STEPS_PER_MEASURE * stepDur;
        const total = measuresRef.current * measureDur;
        const seconds = Tone.getTransport().seconds;
        const pos = loopRef.current ? (seconds % total) : seconds;
        const currentMeasure = Math.floor(pos / measureDur);
        const nextBarTime = (currentMeasure + 1) * measureDur;

        // Cancel all notes from the next bar onwards so none leak through
        Tone.getTransport().cancel(nextBarTime);

        pendingSeekRef.current = measureIndex;
        setPendingSeekMeasure(measureIndex);

        // Fire the jump at the exact bar boundary in the audio timeline
        const eventId = Tone.getTransport().scheduleOnce(() => {
          const target = pendingSeekRef.current;
          if (target === null) return;
          // Bridge back to main thread (transport callbacks run in audio thread)
          setTimeout(() => {
            if (pendingSeekRef.current === null) return;
            const t = pendingSeekRef.current;
            pendingSeekRef.current = null;
            pendingSeekEventRef.current = null;
            setPendingSeekMeasure(null);
            startPlaybackRef.current(t);
          }, 0);
        }, nextBarTime);
        pendingSeekEventRef.current = eventId as unknown as number;
      } else {
        startPlayback(measureIndex);
      }
    },
    [transportState, startPlayback],
  );

  // ── Derived ────────────────────────────────────────────────────────────────

  const isIdle = transportState === 'idle';
  const isRecording = transportState === 'recording';
  const isPlaying = transportState === 'playing';
  const isCountingDown = countdown !== null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="sk-composer">
      {/* Toolbar */}
      <div className="sk-composer-toolbar">
        <div className="sk-bpm-control">
          <span className="pixel-font sk-bpm-label">BPM</span>
          <button className="sk-bpm-btn" onClick={() => handleBpmChange(bpm - 1)} disabled={!isIdle || isCountingDown} aria-label="Decrease BPM">−</button>
          <input
            type="number"
            className="ubuntu-font sk-bpm-input"
            value={bpm}
            min={MIN_BPM}
            max={MAX_BPM}
            onChange={e => handleBpmChange(parseInt(e.target.value, 10))}
            disabled={!isIdle || isCountingDown}
            aria-label="BPM value"
          />
          <button className="sk-bpm-btn" onClick={() => handleBpmChange(bpm + 1)} disabled={!isIdle || isCountingDown} aria-label="Increase BPM">+</button>
        </div>

        <div className="sk-bpm-control">
          <span className="pixel-font sk-bpm-label">BARS</span>
          <button className="sk-bpm-btn" onClick={() => handleMeasuresChange(measures - 1)} disabled={!isIdle || isCountingDown} aria-label="Decrease measures">−</button>
          <span className="ubuntu-font sk-measures-value">{measures}</span>
          <button className="sk-bpm-btn" onClick={() => handleMeasuresChange(measures + 1)} disabled={!isIdle || isCountingDown} aria-label="Increase measures">+</button>
        </div>

        <div className="sk-transport" role="group" aria-label="Transport controls">
          <button
            className={`sk-transport-btn sk-btn-record${isRecording || isCountingDown ? ' active' : ''}`}
            onClick={isRecording ? stopTransport : isCountingDown ? () => setCountdown(null) : async () => { await Tone.start(); setCountdown(4); }}
            disabled={isPlaying}
            title={isRecording ? 'Stop recording' : isCountingDown ? 'Cancel' : 'Record'}
            aria-label={isRecording ? 'Stop recording' : isCountingDown ? 'Cancel countdown' : 'Record'}
          >
            {isRecording ? '⏹' : '⏺'}
          </button>
          <button
            className={`sk-transport-btn sk-btn-play${isPlaying ? ' active' : ''}`}
            onClick={() => startPlayback()}
            disabled={isPlaying || isRecording || isCountingDown}
            title="Play"
            aria-label="Play"
          >
            ▶
          </button>
          <button
            className="sk-transport-btn sk-btn-stop"
            onClick={stopTransport}
            disabled={isIdle && !isCountingDown}
            title="Stop"
            aria-label="Stop"
          >
            ⏹
          </button>
        </div>

        <button
          className={`sk-loop-btn pixel-font${loop ? ' active' : ''}`}
          onClick={() => setLoop(!loop)}
          disabled={!isIdle || isCountingDown}
          aria-label={loop ? 'Disable loop' : 'Enable loop'}
        >
          LOOP {loop ? 'ON' : 'OFF'}
        </button>

        <button
          className="sk-note-dur-btn pixel-font"
          onClick={() => setNoteDurationIndex(i => (i + 1) % NOTE_DURATIONS.length)}
          disabled={!isIdle || isCountingDown}
          aria-label="Cycle note length"
          title="Note length when placing notes"
        >
          {NOTE_DURATIONS[noteDurationIndex].label}
        </button>

        <button
          className="sk-clear-btn pixel-font"
          onClick={() => { if (window.confirm('Clear all notes?')) setNotes([]); }}
          disabled={!isIdle || isCountingDown}
          aria-label="Clear all notes"
        >
          CLEAR
        </button>
      </div>

      {/* Countdown overlay */}
      {isCountingDown && (
        <div className="sk-countdown-overlay" aria-live="assertive" aria-label={`Recording starts in ${countdown}`}>
          <span className="sk-countdown-number pixel-font" key={countdown}>{countdown}</span>
        </div>
      )}

      {/* Status bar */}
      {isRecording && (
        <div className="sk-status sk-status--recording" role="status">
          <span className="sk-status-dot" aria-hidden="true" />
          <span className="pixel-font">REC — play notes on the piano below!</span>
        </div>
      )}
      {isPlaying && (
        <div className="sk-status sk-status--playing" role="status">
          <span className="pixel-font">♪ PLAYING ♪</span>
        </div>
      )}

      {/* DAW Grid */}
      <div className="sk-daw" ref={scrollRef}>
        <div className="sk-daw-content">
          {/* Corner */}
          <div className="sk-daw-corner pixel-font" style={{ width: LABEL_WIDTH, height: HEADER_HEIGHT }}>♪</div>

          {/* Measure number headers */}
          <div className="sk-daw-header" style={{ height: HEADER_HEIGHT, width: gridWidth }}>
            {Array.from({ length: measures }, (_, i) => (
              <div
                key={i}
                className={`sk-daw-measure-num pixel-font${transportState !== 'recording' ? ' sk-daw-measure-num--clickable' : ''}${pendingSeekMeasure === i ? ' sk-daw-measure-num--pending' : ''}`}
                style={{ width: STEPS_PER_MEASURE * STEP_WIDTH }}
                onClick={() => handleHeaderClick(i)}
              >
                {i + 1}
              </div>
            ))}
          </div>

          {/* Note labels */}
          <div className="sk-daw-labels" style={{ width: LABEL_WIDTH }}>
            {labelElements}
          </div>

          {/* Grid area */}
          <div
            className="sk-daw-grid"
            ref={gridRef}
            style={{ width: gridWidth, height: gridHeight }}
            onPointerDown={handleGridPointerDown}
          >
            {rowBackgrounds}
            {verticalLines}

            {notes.map(n => {
              const row = noteToRow(n.note);
              if (row === -1) return null;
              return (
                <div
                  key={n.id}
                  className={`sk-daw-note${dragState?.noteId === n.id && dragMovedRef.current ? ' sk-daw-note--resizing' : ''}`}
                  style={{
                    left: n.startStep * STEP_WIDTH + 1,
                    top: row * ROW_HEIGHT + 1,
                    width: n.durationSteps * STEP_WIDTH - 2,
                    height: ROW_HEIGHT - 2,
                  }}
                >
                  <div className="sk-daw-note-handle" />
                </div>
              );
            })}

            <div ref={playheadRef} className="sk-daw-playhead" style={{ display: 'none', height: gridHeight }} />
          </div>
        </div>
      </div>
    </div>
  );
};
